import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  type CreateOrderDTO,
  OrderCurrency,
  OrderStatus,
} from '@invenco/sales-interface/interfaces';

import { Page } from '@/components/layout';
import {
  WizardActions,
  WizardCancel,
  WizardContent,
  WizardHeader,
  WizardProgress,
  WizardRoot,
  WizardTitle,
} from '@/components/wizard';
import type { WizardStepDefinition } from '@/components/wizard/wizard-context';
import { useGateways } from '@/gateways/GatewayProvider';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { useMessages } from '@/shared/providers/MessagesProvider';

import { CustomerStep } from './customer-step/customer-step';
import { ItemsStep } from './items-step';
import { NotesStep } from './notes-step';
import { PropertiesStep } from './properties-step';
import { ReferenceStep } from './reference-step';
import { ShippingStep } from './shipping-step';

const steps = [
  { id: 'items', label: 'Items', required: true },
  { id: 'customer', label: 'Customer', required: true },
  { id: 'properties', label: 'Properties', required: true },
  { id: 'shipping', label: 'Shipping', required: true },
  { id: 'reference', label: 'Reference' },
  { id: 'notes', label: 'Notes' },
] satisfies WizardStepDefinition[];

const getOrderCreateDTO = (order: Partial<CreateOrderDTO>) => {
  return {
    status: OrderStatus.DRAFT,
    channelName: 'Default',
    orderLines: order.orderLines ?? [],
    currency: order.currency,
    allowPartialFulfillments: order.allowPartialFulfillments,
    fulfillmentProfileId: order.fulfillmentProfileId ?? null,
    fulfillmentProfileName: order.fulfillmentProfileName ?? null,
    shippingProfileName: order.shippingProfileName,
    subtotalShipping: order.subtotalShipping,
    ...(order.customerId ? { customerId: order.customerId } : {}),
    customerName: order.customerName,
    email: order.email,
    phone: order.phone,
    shippingAddress: order.shippingAddress,
    billingAddress: order.billingAddress,
    ...(order.reference ? { reference: order.reference } : {}),
    ...(order.notes ? { notes: order.notes } : {}),
  } as CreateOrderDTO;
};

export function OrderWizard() {
  const navigate = useNavigate();
  const messages = useMessages();
  const { salesGateway } = useGateways();
  const draftRef = useRef<Partial<CreateOrderDTO>>({});
  const [channelName, setChannelName] = useState('Default');
  const [currency, setCurrency] = useState(OrderCurrency.AUD);

  const { mutate: createOrder } = useGatewayMutation({
    mutationFn: () => salesGateway.createOrder(getOrderCreateDTO(draftRef.current)),
    onSuccess: (order) => {
      messages.success('Created new draft order', { name: order.name });
      void navigate(`/sales/orders-shad/${order.id}`);
    },
    linkedQuery: ['orders', undefined],
  });

  const updateDraft = (input: Partial<CreateOrderDTO>) => {
    draftRef.current = { ...draftRef.current, ...input };
  };

  return (
    <Page>
      <WizardRoot steps={steps}>
        <WizardHeader>
          <WizardCancel to="/sales/orders" />
          <WizardTitle>New order</WizardTitle>
          <WizardProgress />
          <WizardActions
            submitLabel="Create order"
            onSubmit={async () => {
              await createOrder();
            }}
          />
        </WizardHeader>

        <WizardContent>
          <ItemsStep step={steps[0]} currency={currency} onChange={updateDraft} />
          <CustomerStep step={steps[1]} onChange={updateDraft} />
          <PropertiesStep
            step={steps[2]}
            onChange={updateDraft}
            onChannelNameChange={setChannelName}
            onCurrencyChange={setCurrency}
          />
          <ShippingStep step={steps[3]} channelName={channelName} onChange={updateDraft} />
          <ReferenceStep step={steps[4]} onChange={updateDraft} />
          <NotesStep step={steps[5]} onChange={updateDraft} />
        </WizardContent>
      </WizardRoot>
    </Page>
  );
}
